import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';

interface DataPoint {
  date: string;
  slot: string;
  ph: number | null;
  [key: string]: string | number | boolean | null | undefined;
}

interface PHBarChartProps {
  data: DataPoint[];
  height?: number;
}

interface DayData {
  date: string;
  amPh: number | null;
  pmPh: number | null;
  variation: number | null;
  variationStatus: 'ideal' | 'caution' | 'problem' | null;
}

function getVariationStatus(variation: number): 'ideal' | 'caution' | 'problem' {
  if (variation <= 0.3) return 'ideal';
  if (variation <= 0.5) return 'caution';
  return 'problem';
}

const VARIATION_COLORS = {
  ideal: '#22c55e',    // green-500
  caution: '#f59e0b',  // amber-500
  problem: '#ef4444',  // red-500
};

export function PHBarChart({ data, height = 300 }: PHBarChartProps) {
  // Group readings by date, extract AM (7am) and PM (3pm) pH values
  const dayMap = new Map<string, { am: number | null; pm: number | null }>();

  for (const d of data) {
    if (d.ph === null || d.ph === undefined) continue;
    const phNum = typeof d.ph === 'string' ? parseFloat(d.ph) : d.ph;
    if (isNaN(phNum)) continue;
    const existing = dayMap.get(d.date) || { am: null, pm: null };
    if (d.slot === '7am') {
      existing.am = phNum;
    } else if (d.slot === '3pm') {
      // Afternoon pH is collected at 3pm in the authoritative model
      existing.pm = phNum;
    }
    dayMap.set(d.date, existing);
  }

  const chartData: DayData[] = Array.from(dayMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, { am, pm }]) => {
      const variation = am !== null && pm !== null ? Math.abs(pm - am) : null;
      return {
        date,
        amPh: am,
        pmPh: pm,
        variation,
        variationStatus: variation !== null ? getVariationStatus(variation) : null,
      };
    });

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center text-gray-400" style={{ height }}>
        No pH data available
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* Acceptable pH range band */}
          <ReferenceArea y1={7.5} y2={8.5} fill="#dcfce7" fillOpacity={0.5} />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={(v) => {
              const date = new Date(v);
              const currentYear = new Date().getFullYear();
              return date.getFullYear() !== currentYear
                ? format(date, 'MMM d, yy')
                : format(date, 'MMM d');
            }}
          />
          <YAxis
            domain={[6.5, 9.5]}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={(v) => v.toFixed(1)}
          />
          <Tooltip content={<PHTooltip />} />

          <Bar dataKey="amPh" name="AM pH (7am)" fill="#93c5fd" radius={[2, 2, 0, 0]} maxBarSize={20} />
          <Bar dataKey="pmPh" name="PM pH" fill="#3b82f6" radius={[2, 2, 0, 0]} maxBarSize={20} />
        </BarChart>
      </ResponsiveContainer>

      {/* Variation indicators below chart */}
      <div className="flex items-center gap-1 mt-2 px-[60px] overflow-x-auto">
        {chartData.map((day) => (
          <div key={day.date} className="flex flex-col items-center min-w-[40px] flex-1">
            {day.variation !== null && day.variationStatus !== null ? (
              <span
                className="text-[10px] font-medium px-1 py-0.5 rounded"
                style={{
                  color: VARIATION_COLORS[day.variationStatus],
                  backgroundColor: `${VARIATION_COLORS[day.variationStatus]}15`,
                }}
              >
                {day.variation.toFixed(2)}
              </span>
            ) : (
              <span className="text-[10px] text-gray-300">—</span>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-gray-100">
        <LegendItem color="#93c5fd" label="AM pH (7am)" />
        <LegendItem color="#3b82f6" label="PM pH" />
        <LegendItem color="#dcfce7" label="OK range (7.5–8.5)" filled />
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="text-green-500 font-medium">0–0.3</span>
          <span className="text-amber-500 font-medium">0.3–0.5</span>
          <span className="text-red-500 font-medium">&gt;0.5</span>
          <span className="text-gray-400 ml-0.5">variation</span>
        </div>
      </div>
    </div>
  );
}

function PHTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DayData }> }) {
  if (!active || !payload?.length) return null;
  const day = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-sm font-medium text-gray-900 mb-2">
        {format(new Date(day.date), 'MMM d, yyyy')}
      </p>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">AM pH (7am)</span>
          <span className="font-medium">{day.amPh?.toFixed(2) ?? '—'}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">PM pH</span>
          <span className="font-medium">{day.pmPh?.toFixed(2) ?? '—'}</span>
        </div>
        {day.variation !== null && (
          <div className="flex justify-between gap-4 pt-1 border-t border-gray-100">
            <span className="text-gray-500">Variation</span>
            <span
              className="font-semibold"
              style={{ color: day.variationStatus ? VARIATION_COLORS[day.variationStatus] : '#6b7280' }}
            >
              {day.variation.toFixed(2)}
              {day.variationStatus && (
                <span className="ml-1 text-xs font-normal">
                  ({day.variationStatus})
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function LegendItem({
  color,
  label,
  filled,
}: {
  color: string;
  label: string;
  filled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <div
        className="w-3 h-3 rounded-sm"
        style={filled ? { backgroundColor: color } : { backgroundColor: color }}
      />
      {label}
    </div>
  );
}
